import React, { useEffect, useRef, useState } from 'react';
import { fetchAuditoriaProposta } from '../../../api/portalCotacoes/portalCotacoesApi';
import { AuditoriaProposta } from '../../../interfaces/PortalCotacoes';
import { notify } from '../../../shared/feedback/notify';
// Receita visual dos modais de detalhe da Auditoria (o de Planejamento e o de Liberação
// Administrativa usam a mesma folha) — reuso por import, nunca cópia.
import '../../../components/modal/planning-comparison/PlanningComparisonModal.css';
import './AuditoriaPropostaModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Proposta do fornecedor como ela era no evento auditado (14/08/2026, pedido do Victor:
 * "dá para criar um menu que visualize a proposta, como o da Liberação Administrativa?").
 *
 * ⚠️ Não é o `PropostaRecebidaModal` do detalhe da cotação, por duas razões que a auditoria
 * torna incontornáveis:
 *
 * 1. **Papel** — aquele modal se alimenta do `GET /cotacoes/:id` (COMPRA/ADMIN/TI); aqui a
 *    tela é aberta ao CHEFE_DE_OFICINA, que tomaria 403.
 * 2. **Versão** — aquele mostra sempre a proposta ATIVA. Auditoria pergunta o que o
 *    fornecedor enviou **naquele momento**, e o evento pode ser de uma versão depois
 *    substituída. Mostrar a atual no lugar seria mentir sobre o passado.
 */

interface AuditoriaPropostaModalProps {
  propostaId: string;
  onFechar: () => void;
}

function moeda(valor: number | null): string {
  if (valor === null) return '—';
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function prazo(dias: number | null): string {
  if (dias === null) return '—';
  if (dias === 0) return 'Pronta entrega';
  return `${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}

function dataHora(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

const AuditoriaPropostaModal: React.FC<AuditoriaPropostaModalProps> = ({ propostaId, onFechar }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [proposta, setProposta] = useState<AuditoriaProposta | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) onFechar();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onFechar]);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const dados = await fetchAuditoriaProposta(propostaId);
        if (!ativo) return;
        setProposta(dados);
        setErro(null);
      } catch (error) {
        if (!ativo) return;
        /*
         * Modal em branco leria como "proposta sem itens" — uma afirmação sobre o que o
         * fornecedor enviou, tirada de uma falha de rede. O aviso fica DENTRO do modal
         * porque é onde o usuário está olhando (o toast some).
         */
        setProposta(null);
        setErro('Não foi possível carregar a proposta. Feche e tente de novo; se persistir, acione a TI.');
        notify.errorFrom(error, 'Não foi possível carregar a proposta.');
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [propostaId]);

  const totalCotado = (proposta?.itens ?? []).reduce((soma, i) => soma + (i.totalItem ?? 0), 0);
  const itensCotados = (proposta?.itens ?? []).filter((i) => i.cotou).length;

  return (
    <div className="planning-modal-overlay">
      <div className="planning-modal-content" ref={modalRef}>
        <div className="planning-modal-header">
          <h2>Proposta do Fornecedor</h2>
          <button className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>

        {proposta && (
          <div className="planning-modal-metadata">
            <div className="metadata-item">
              <strong>Fornecedor:</strong> <span>{proposta.fornecedorNome}</span>
            </div>
            <div className="metadata-item">
              <strong>Enviada em:</strong> <span>{dataHora(proposta.enviadaEm)}</span>
            </div>
            <div className="metadata-item">
              <strong>Versão:</strong> <span>{proposta.versao}</span>
            </div>
          </div>
        )}

        <div className="planning-modal-body">
          {carregando && <p className="auditoria-proposta-vazio">Carregando a proposta...</p>}

          {erro && (
            <p className="auditoria-proposta-erro" role="alert">
              {erro}
            </p>
          )}

          {proposta && (
            <>
              {/*
                Aviso de versão superada: sem ele, quem audita lê uma proposta antiga como
                se fosse a que valeu na decisão.
              */}
              {proposta.status === 'SUBSTITUIDA' && (
                <p className="auditoria-proposta-aviso">
                  Esta versão foi <strong>substituída</strong> por um reenvio posterior do fornecedor. Ela é
                  mostrada como estava no momento do evento.
                </p>
              )}

              <div className="auditoria-proposta-resumo">
                <div>
                  <strong>Cotação:</strong> {proposta.cotacaoNumero}
                </div>
                <div>
                  <strong>CNPJ:</strong> {proposta.fornecedorCnpj}
                </div>
                <div>
                  <strong>Condição de pagamento:</strong> {proposta.condicaoPagamento || '—'}
                </div>
                <div>
                  <strong>Itens cotados:</strong> {itensCotados} de {proposta.itens.length}
                </div>
              </div>

              <div className="auditoria-proposta-tabela-wrapper">
                <table className="auditoria-proposta-tabela">
                  <thead>
                    <tr>
                      <th>Peça</th>
                      <th>Qtd.</th>
                      <th>Preço unit.</th>
                      <th>Total</th>
                      <th>Prazo</th>
                      <th>Marca</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proposta.itens.map((item) => (
                      <tr key={item.codigoProduto + item.descricao}>
                        <td>
                          <div>{item.descricao}</div>
                          <div className="auditoria-proposta-secundario">{item.codigoProduto}</div>
                          {item.observacaoItem && (
                            <div className="auditoria-proposta-secundario">{item.observacaoItem}</div>
                          )}
                        </td>
                        <td>
                          {item.quantidade} {item.unidade}
                        </td>
                        {/*
                          "Não cotou" ocupa a linha inteira dos valores: preço zerado ou
                          traço solto se confundiria com preço não informado, e a diferença
                          importa — quem não cotou fica fora do ranking do item.
                        */}
                        {item.cotou ? (
                          <>
                            <td>{moeda(item.precoUnitario)}</td>
                            <td>{moeda(item.totalItem)}</td>
                            <td>{prazo(item.prazoEntregaDias)}</td>
                            <td>{item.marcaFabricante || '—'}</td>
                          </>
                        ) : (
                          <td colSpan={4} className="auditoria-proposta-nao-cotou">
                            Não cotou — o fornecedor informou que não tem esta peça
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3}>
                        <strong>Total dos itens cotados</strong>
                      </td>
                      <td colSpan={3}>
                        <strong>{moeda(totalCotado)}</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {proposta.observacao && (
                <div className="auditoria-proposta-observacao">
                  <strong>Observação do fornecedor:</strong>
                  <p>{proposta.observacao}</p>
                </div>
              )}

              {/*
                at-11 (28/09/2026): o frete passou a ser informado pelo fornecedor e a entrar
                no total da cesta. Esta timeline mostra os valores POR ITEM da proposta, onde
                o frete nunca entra (ele é da proposta inteira) — o texto diz isso, em vez do
                antigo "os valores não incluem frete", que hoje se leria como "o sistema não
                coleta frete".
              */}
              <p className="auditoria-proposta-nota">
                Os valores acima são por item. O frete é da proposta inteira e entra no total da cesta, na tela
                da cotação.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditoriaPropostaModal;
